import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProductAttributeDefinitionDetailsResponse, AttributeDefinitionRequest, ProductAttributeDefinitionsService } from '@src/app/shared/backend/clients';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { SessionService } from '@src/app/shared/services/session.service';
import { ProductAttributeDataTypeService } from '@src/app/shared/services/productAttributeDataType.service';
import { AttributeDataType } from '@src/app/shared/enums/AttributeDataType.enum';
import { TranslateService } from '@ngx-translate/core';

interface LanguageItem {
  id?: number;
  language: string;
  code: string;
}

@Component({
    selector: 'app-edit-product-attribute-definitions',
    templateUrl: './edit-product-attribute-definitions.component.html',
    styleUrl: './edit-product-attribute-definitions.component.scss',
    host: {
        class: 'app-edit-product-attribute-definitions'
    },
    standalone: false
})
export class EditProductAttributeDefinitionsComponent extends BaseViewComponent implements OnInit {
  productAttributeDefinitionId = 0;
  productAttributesDefinitions: ProductAttributeDefinitionDetailsResponse | undefined;

  languages: LanguageItem[] = [];
  dataTypeOptions = this.productAttributeDataTypeService.getProductAttributeDataTypeOptions();
  showAttributeTranslations = false;

  productAttributeDefinitionForm: FormGroup = this.fb.group(
    {
      attributeCode: ['', [Validators.required, Validators.maxLength(100)]],
      dataType: [{ value: [AttributeDataType.FIXED_VALUES], disabled: true }, Validators.required],
      allowMultipleValues: [{ value: false, disabled: true }, Validators.required],
      changesOverTime: [{ value: false, disabled: true }, Validators.required],
      attributeTranslations: this.fb.group({}),
      valueCodes: this.fb.array([])
    },
    {
      validators: [this.requireValueCodeForFixedValuesType()]
    }
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private translate: TranslateService,
    private session: SessionService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
    private productAttributeDataTypeService: ProductAttributeDataTypeService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    await this.loadLanguages();
    this.buildAttributeTranslationsGroup();
    this.handleDataTypeChanges();

    this.route.params.subscribe(async params => {
      this.productAttributeDefinitionId = Number(params['productAttributeDefinitionId']);

      if (this.productAttributeDefinitionId > 0) {
        await this.get();
      }
    });
  }

  get valueCodes(): FormArray {
    return this.productAttributeDefinitionForm.get('valueCodes') as FormArray;
  }

  get attributeTranslationsGroup(): FormGroup {
    return this.productAttributeDefinitionForm.get('attributeTranslations') as FormGroup;
  }

  get isFixedValuesType(): boolean {
    return this.productAttributeDefinitionForm.get('dataType')?.value?.[0] === AttributeDataType.FIXED_VALUES;
  }

  mapApiDataTypeToFormValue(dataType: string): AttributeDataType {
    // bugfix: due to i18n pipe cannot bind the response directly to the dropdown
    switch (dataType) {
      case 'FIXED_VALUES':
        return AttributeDataType.FIXED_VALUES;
      case 'DECIMAL':
        return AttributeDataType.DECIMAL;
      case 'INTEGER':
        return AttributeDataType.INTEGER;
      case 'TEXT':
        return AttributeDataType.TEXT;
      default:
        return AttributeDataType.FIXED_VALUES;
    }
  }

  override async get(): Promise<void> {
    this.productAttributesDefinitions = await firstValueFrom(
      this.productAttributeDefinitionsService.apiProductAttributeDefinitionsDetailsIdGet(
        this.productAttributeDefinitionId
      )
    );

    this.patchForm(this.productAttributesDefinitions);
  }

  patchForm(data: ProductAttributeDefinitionDetailsResponse): void {
    this.productAttributeDefinitionForm.patchValue({
      attributeCode: data.definitionCode,
      dataType: [this.mapApiDataTypeToFormValue(data.productAttributeDataType)],
      allowMultipleValues: data.allowMultipleValues,
      changesOverTime: data.changesOverTime
    });

    data.productAttributeDefinitionTranslations?.forEach(translation => {
      this.attributeTranslationsGroup
        .get(translation.isoCode)
        ?.setValue(translation.translatedName);
    });

    this.showAttributeTranslations = !!data.productAttributeDefinitionTranslations?.length;

    this.clearValueCodes();

    data.productAttributeValues?.forEach(value => {
      const group = this.createValueCodeGroup();

      group.patchValue({
        code: value.valueCode,
        codeIsActive: value.isActive,
        showTranslations: !!value.productAttributeValueTranslations?.length,
        newValue: true
      });

      const translationsGroup = group.get('translations') as FormGroup;

      value.productAttributeValueTranslations?.forEach(translation => {
        translationsGroup
          .get(translation.isoCode)
          ?.setValue(translation.translatedValue);
      });

      this.valueCodes.push(group);
    });

    if (this.isFixedValuesType && this.valueCodes.length === 0) {
      this.addValueCode();
    }

    this.productAttributeDefinitionForm.updateValueAndValidity();
  }

  private async loadLanguages(): Promise<void> {
    const languages = this.session.companySettings?.languages ?? [];
    const enabledLanguages = languages.filter(language => language.isEnabled);

    return new Promise(resolve => {
      this.translate.get('PORTAL_LANGUAGES').subscribe(() => {
        this.languages = enabledLanguages
          .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
          .map(lang => {
            const code = lang.code || 'unknown';

            return {
              id: lang.id,
              language: `PORTAL_LANGUAGES.${code.toUpperCase()}`,
              code
            };
          });

        resolve();
      });
    });
  }

  private buildAttributeTranslationsGroup(): void {
    const group = this.fb.group({});

    this.languages.forEach(language => {
      group.addControl(language.code, this.fb.control(''));
    });

    this.productAttributeDefinitionForm.setControl('attributeTranslations', group);
  }

  private createTranslationsGroup(): FormGroup {
    const group = this.fb.group({});

    this.languages.forEach(language => {
      group.addControl(language.code, this.fb.control(''));
    });

    return group;
  }

  private createValueCodeGroup(): FormGroup {
    return this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(100)]],
      codeIsActive: [true, Validators.required],
      showTranslations: [false],
      translations: this.createTranslationsGroup(),
      newValue: [false]
    });
  }

  private handleDataTypeChanges(): void {
    this.productAttributeDefinitionForm.get('dataType')?.valueChanges.subscribe(() => {
      if (this.isFixedValuesType) {
        if (this.valueCodes.length === 0) {
          this.addValueCode();
        }
      } else {
        this.showAttributeTranslations = false;
        this.clearValueCodes();
      }

      this.productAttributeDefinitionForm.updateValueAndValidity();
    });
  }

  private clearValueCodes(): void {
    while (this.valueCodes.length > 0) {
      this.valueCodes.removeAt(0);
    }
  }

  private requireValueCodeForFixedValuesType(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const dataTypeControlValue = control.get('dataType')?.value;
      const selectedType = Array.isArray(dataTypeControlValue) ? dataTypeControlValue[0] : null;
      const valueCodes = control.get('valueCodes') as FormArray | null;

      if (selectedType !== AttributeDataType.FIXED_VALUES) {
        return null;
      }

      if (!valueCodes || valueCodes.length === 0) {
        return { valueCodeRequired: true };
      }

      const hasAtLeastOneValue = valueCodes.controls.some(item => {
        const code = item.get('code')?.value;
        return !!String(code ?? '').trim();
      });

      return hasAtLeastOneValue ? null : { valueCodeRequired: true };
    };
  }

  addValueCode(): void {
    this.valueCodes.push(this.createValueCodeGroup());
    this.productAttributeDefinitionForm.updateValueAndValidity();
  }

  removeValueCode(index: number): void {
    this.valueCodes.removeAt(index);
    this.productAttributeDefinitionForm.updateValueAndValidity();
  }

  toggleAttributeTranslations(): void {
    if (!this.isFixedValuesType) {
      return;
    }

    this.showAttributeTranslations = !this.showAttributeTranslations;
  }

  toggleValueTranslations(index: number): void {
    const group = this.valueCodes.at(index) as FormGroup;
    const currentValue = group.get('showTranslations')?.value ?? false;

    group.get('showTranslations')?.setValue(!currentValue);
  }

  getValueTranslationsGroup(index: number): FormGroup {
    return this.valueCodes.at(index).get('translations') as FormGroup;
  }

  async submitForm(): Promise<void> {
    this.productAttributeDefinitionForm.markAllAsTouched();
    this.productAttributeDefinitionForm.updateValueAndValidity();

    if (this.productAttributeDefinitionForm.invalid) {
      return;
    }

    const payload: AttributeDefinitionRequest = {
      definitionCode: String(this.productAttributeDefinitionForm.get('attributeCode')?.value ?? '').trim(),
      attributeDataType: this.productAttributeDefinitionForm.get('dataType')?.value?.[0] ?? 0,
      allowMultipleValues: !!this.productAttributeDefinitionForm.get('allowMultipleValues')?.value,
      changesOverTime: !!this.productAttributeDefinitionForm.get('changesOverTime')?.value,
      attributeDefinitionTranslations: this.isFixedValuesType
        ? this.languages
            .map(language => ({
              isoCode: language.code,
              translatedName: String(this.attributeTranslationsGroup.get(language.code)?.value ?? '').trim()
            }))
            .filter(item => !!item.translatedName)
        : [],
      attributeValues: this.isFixedValuesType
        ? this.valueCodes.controls
            .map(control => {
              const group = control as FormGroup;
              const translationsGroup = group.get('translations') as FormGroup;

              return {
                valueCode: String(group.get('code')?.value ?? '').trim(),
                isActive: group.get('codeIsActive')?.value ?? '',
                attributeValueTranslations: this.languages
                  .map(language => ({
                    isoCode: language.code,
                    translatedValue: String(translationsGroup.get(language.code)?.value ?? '').trim()
                  }))
                  .filter(item => !!item.translatedValue)
              };
            })
            .filter(item => !!item.valueCode)
        : []
    };

    await firstValueFrom(
      this.productAttributeDefinitionsService.apiProductAttributeDefinitionsUpdateIdPut(
        this.productAttributeDefinitionId,
        payload
      )
    );

    this.router.navigate([`/product-attribute-definitions/${this.productAttributeDefinitionId}`]);
  }
}
